import { useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../../api';
import { Segmented, StatusPill, arrowNav } from '../../ui/bits';
import { DateField, Select, TimeField, type SelectOption } from '../../ui/controls';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { useOpsShops } from '../queries';
import { AdPreview } from './AdPreview';
import { Checklist, NotReadyPanel } from './Checklist';
import {
  BLOCKED_CATEGORIES,
  CATEGORY_OPTIONS,
  GATED_CATEGORIES,
  LINE_TARGETS,
  LINK_OPTIONS,
  MAX_ADVERTISER,
  MAX_CLEARANCE,
  MAX_DEAL,
  MAX_HEADLINE,
  MAX_PERMIT,
  PLATES,
  PLATE_KEYS,
  STATUS_VIEW,
  adPatch,
  draftOf,
  emptyDraft,
  imageAdvice,
  imageProblem,
  missingLabel,
  readiness,
  statusActions,
  uploadForm,
  validateAd,
  videoProblem,
} from './lib.js';
import { useAdStatus, useStoreAd } from './queries';
import type { AdCategory, AdKind, AdLinkKind, AdPlate, OpsAd } from './types';

type Draft = ReturnType<typeof emptyDraft>;
type Errors = Partial<Record<keyof Draft | 'schedule' | 'image' | 'video' | 'form', string>>;
type Staged = { file: File; url: string; advice: string[] };

const KIND_OPTIONS: { value: AdKind; label: string }[] = [
  { value: 'photo', label: 'Photo' },
  { value: 'video', label: 'Video' },
];

/** Category options: the six we take, then the ones we never take (disabled, never sent). */
const CATEGORY_SELECT: SelectOption[] = [
  ...CATEGORY_OPTIONS,
  ...BLOCKED_CATEGORIES.map((label, i) => ({ value: `blocked-${i}`, label: `Not accepted: ${label}`, disabled: true })),
];

const CATEGORY_HELP: Partial<Record<AdCategory, string>> = {
  alcohol: 'Alcohol ads need an ASC clearance code before they go live.',
  otc: 'OTC medicine ads need an ASC clearance code before they go live.',
  supplement: 'Supplement ads need an ASC clearance code and the FDA "MAHALAGANG PAALALA" warning (or the FDA registration) in the permit line.',
};

/** A local URL for a picked file ('' where the browser has none). */
function objectUrl(file: File) {
  try {
    return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
  } catch {
    return '';
  }
}
const dropUrl = (url: string) => void (url && typeof URL.revokeObjectURL === 'function' && URL.revokeObjectURL(url));

/** The browser's reading of a picked file; null when it can't tell (the server checks again). */
function imageSize(url: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    const t = window.setTimeout(() => resolve(null), 4000);
    img.onload = () => {
      window.clearTimeout(t);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      window.clearTimeout(t);
      resolve(null);
    };
    img.src = url;
  });
}
function videoSeconds(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const v = document.createElement('video');
    const t = window.setTimeout(() => resolve(null), 4000);
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => {
      window.clearTimeout(t);
      resolve(Number.isFinite(v.duration) ? v.duration : null);
    };
    v.onerror = () => {
      window.clearTimeout(t);
      resolve(null);
    };
    v.src = url;
  });
}

function Err({ id, children }: { id: string; children?: string }) {
  if (!children) return null;
  return (
    <span id={id} className="pill st-error err-pill" role="alert">
      {children}
    </span>
  );
}

function Field({ id, label, error, help, children, side }: { id: string; label: string; error?: string; help?: ReactNode; children: ReactNode; side?: ReactNode }) {
  return (
    <div className="field">
      <div className="ad-labelrow">
        <label htmlFor={id}>{label}</label>
        {side}
      </div>
      {children}
      {help ? (
        <span className="help" id={`${id}-help`}>
          {help}
        </span>
      ) : null}
      <Err id={`${id}-err`}>{error}</Err>
    </div>
  );
}

/** New ad / edit ad: every OpsAdInput field, the uploads, the live card preview and the status buttons. */
export function AdEditor({ ad, onSaved, onClose }: { ad: OpsAd | null; onSaved(ad: OpsAd): void; onClose(): void }) {
  const toast = useToast();
  const store = useStoreAd();
  const status = useAdStatus();
  const shops = useOpsShops();
  const [blank] = useState(() => emptyDraft(Date.now()));
  // `base` is the saved ad the form started from. A different ad resets the form; a newer copy of
  // the same ad (the list refetches) only does when nothing has been typed yet.
  const [base, setBase] = useState<OpsAd | null>(ad);
  const initial = useMemo(() => (base ? draftOf(base) : blank), [base, blank]);
  const [d, setD] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [image, setImage] = useState<Staged | null>(null);
  const [video, setVideo] = useState<Staged | null>(null);
  const [saving, setSaving] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  const typed = JSON.stringify(d) !== JSON.stringify(initial);
  if ((ad?.id ?? null) !== (base?.id ?? null)) {
    setBase(ad);
    setD(ad ? draftOf(ad) : blank);
    setErrors({});
    setImage(null);
    setVideo(null);
  } else if (ad && base && ad.updatedAt !== base.updatedAt && !typed) {
    setBase(ad);
    setD(draftOf(ad));
  }
  // Object URLs for staged files die with them.
  useEffect(() => () => void (image && dropUrl(image.url)), [image]);
  useEffect(() => () => void (video && dropUrl(video.url)), [video]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => {
    setD((x) => ({ ...x, [k]: v }));
    if (errors[k] || (errors.schedule && /^(start|end)/.test(k))) setErrors(({ [k]: _a, schedule: _s, ...rest }) => rest as Errors);
  };
  const input = (k: keyof Draft) => (e: ChangeEvent<HTMLInputElement> | { target: { value: string } }) => set(k, e.target.value as never);
  const invalid = (k: keyof Errors) => (errors[k] ? true : undefined);
  const described = (id: string, k: keyof Errors, help = false) => [help ? `${id}-help` : '', errors[k] ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined;

  const dirty = typed || !!image || !!video;
  const shopList = shops.data ?? [];
  const vendorOptions: SelectOption[] = [{ value: '', label: 'Not a PopCart shop' }, ...shopList.map((s) => ({ value: s.id, label: s.name }))];
  const shopOptions: SelectOption[] = shopList.flatMap((s) => (s.slug ? [{ value: s.slug, label: s.name }] : []));
  const headLen = d.headline.trim().length;
  const gated = GATED_CATEGORIES.includes(d.category);

  const pickImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problem = imageProblem(file);
    if (problem) {
      setErrors((x) => ({ ...x, image: problem }));
      return;
    }
    const url = objectUrl(file);
    const dims = await imageSize(url);
    setErrors(({ image: _i, ...rest }) => rest);
    setImage({ file, url, advice: imageAdvice(file, dims) });
  };
  const pickVideo = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const first = videoProblem(file);
    if (first) {
      setErrors((x) => ({ ...x, video: first }));
      return;
    }
    const url = objectUrl(file);
    const secs = await videoSeconds(url);
    const problem = videoProblem(file, secs);
    if (problem) {
      dropUrl(url);
      setErrors((x) => ({ ...x, video: problem }));
      return;
    }
    setErrors(({ video: _v, ...rest }) => rest);
    setVideo({ file, url, advice: secs === null ? ["We couldn't read the length here: keep it 6 to 15 seconds."] : [`${secs.toFixed(1)} s, plays muted.`] });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const r = validateAd(d);
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    setSaving(true);
    let saved: OpsAd | null = ad;
    try {
      if (!ad) saved = await api.createOpsAd(r.value);
      else {
        const patch = adPatch(ad, r.value);
        if (Object.keys(patch).length) saved = await api.updateOpsAd(ad.id, patch);
      }
      if (saved && image) saved = await api.uploadOpsAdImage(saved.id, uploadForm('image', image.file));
      if (saved && video && d.kind === 'video') saved = await api.uploadOpsAdVideo(saved.id, uploadForm('video', video.file));
      if (saved) {
        store(saved);
        setBase(saved);
        setD(draftOf(saved));
        setImage(null);
        setVideo(null);
        toast(ad ? `Saved "${saved.headline}".` : `"${saved.headline}" is saved as a draft.`);
        onSaved(saved);
      }
    } catch (err) {
      if (saved && saved !== ad) {
        // The ad exists (or changed) even though an upload failed: keep editing that one, keep
        // what was typed and the staged file so Save can try the upload again.
        store(saved);
        setBase(saved);
        onSaved(saved);
      }
      setErrors({ form: errorMessage(err, "Couldn't save the ad. Try again.") });
    } finally {
      setSaving(false);
    }
  };

  const imageSrc = image?.url || ad?.imageUrl || null;
  const videoSrc = video?.url || ad?.videoUrl || null;
  const ready = readiness({
    kind: d.kind,
    category: d.category,
    clearanceCode: d.clearanceCode,
    permitText: d.permitText,
    dealLabel: d.dealLabel,
    imageUrl: image ? image.file.name : (ad?.imageUrl ?? null),
    videoUrl: video ? video.file.name : (ad?.videoUrl ?? null),
  });
  // Anything the server lists that the form doesn't know about stays on the list, in plain words.
  const known = new Set(ready.map((r) => r.key.toLowerCase()));
  const extra = !dirty && ad ? ad.missing.filter((m) => !known.has(m.toLowerCase().replace(/[^a-z]/g, '').replace(/^image$/, 'imageurl').replace(/^video$/, 'videourl'))) : [];
  const checklist = [...ready, ...extra.map((m, i) => ({ key: `srv-${i}`, label: missingLabel(m), done: false }))];
  const sv = ad ? STATUS_VIEW[ad.status] : null;

  return (
    <>
      <div className="top">
        <div className="grow stack s4">
          <h1 className="t-title">{ad ? 'Edit ad' : 'New ad'}</h1>
          <span className="t-monocap muted">HOME · TOP SLOT · 343 × 180 · AUTOPLAY, MUTED</span>
        </div>
        {sv ? <StatusPill tone={sv.tone}>{sv.label}</StatusPill> : null}
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          Back to ads
        </button>
      </div>

      <div className="ad-layout">
        <form className="card panel ad-form stack s24" aria-label={ad ? 'Edit ad' : 'New ad'} onSubmit={save} noValidate>
          <fieldset>
            <legend>Advertiser</legend>
            <Field id="ad-advertiser" label="Advertiser name" error={errors.advertiserName} help="Shown in the Ad pill: “Ad · Marikina Fresh Catch”.">
              <input
                id="ad-advertiser"
                className="input"
                autoComplete="off"
                maxLength={MAX_ADVERTISER}
                value={d.advertiserName}
                aria-invalid={invalid('advertiserName')}
                aria-describedby={described('ad-advertiser', 'advertiserName', true)}
                onChange={input('advertiserName')}
              />
            </Field>
            <Field id="ad-vendor" label="PopCart shop (for order attribution)" help="Orders at this shop within 24 h of a tap count for the ad.">
              <Select id="ad-vendor" value={d.vendorId} options={vendorOptions} onChange={input('vendorId')} aria-describedby="ad-vendor-help" />
            </Field>
            <Field id="ad-category" label="Category" error={errors.category} help={CATEGORY_HELP[d.category]}>
              <Select
                id="ad-category"
                value={d.category}
                options={CATEGORY_SELECT}
                onChange={(e) => !e.target.value.startsWith('blocked-') && set('category', e.target.value as AdCategory)}
                aria-describedby={described('ad-category', 'category', !!CATEGORY_HELP[d.category])}
              />
            </Field>
            <p className="ad-blocked">
              <b>Not accepted:</b> {BLOCKED_CATEGORIES.join(', ').toLowerCase().replace(/^./, (c) => c.toUpperCase())}.
            </p>
            {gated || d.clearanceCode ? (
              <Field
                id="ad-clearance"
                label="ASC clearance code"
                error={errors.clearanceCode}
                help={gated ? 'The Ad Standards Council reference. Required before this ad goes live.' : 'Only alcohol, supplement and OTC ads need one.'}
              >
                <input
                  id="ad-clearance"
                  className="input t-mono"
                  autoComplete="off"
                  maxLength={MAX_CLEARANCE}
                  value={d.clearanceCode}
                  aria-invalid={invalid('clearanceCode')}
                  aria-describedby={described('ad-clearance', 'clearanceCode', true)}
                  onChange={input('clearanceCode')}
                />
              </Field>
            ) : null}
          </fieldset>

          <fieldset>
            <legend>The card</legend>
            <div className="field">
              <span className="flabel">Format</span>
              <Segmented<AdKind> label="Format" value={d.kind} options={KIND_OPTIONS} onChange={(v) => set('kind', v)} />
            </div>
            <Field
              id="ad-headline"
              label="Headline"
              error={errors.headline}
              help="Set by us in Archivo on the plate. Keep it short: it has to read in a second."
              side={
                <span className={headLen > MAX_HEADLINE ? 'ad-count over' : 'ad-count'} aria-live="polite">
                  {headLen}/{MAX_HEADLINE}
                </span>
              }
            >
              <input
                id="ad-headline"
                className="input"
                autoComplete="off"
                value={d.headline}
                aria-invalid={invalid('headline') ?? (headLen > MAX_HEADLINE ? true : undefined)}
                aria-describedby={described('ad-headline', 'headline', true)}
                onChange={input('headline')}
              />
            </Field>
            <div className="field">
              <span className="flabel" id="ad-plate-l">
                Plate colour
              </span>
              <div className="ad-plates" role="radiogroup" aria-labelledby="ad-plate-l" onKeyDown={(e) => arrowNav(e)}>
                {PLATE_KEYS.map((k: AdPlate) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={d.plate === k}
                    tabIndex={d.plate === k ? 0 : -1}
                    onClick={() => set('plate', k)}
                    style={{ '--ad-plate': `var(${PLATES[k].token})` } as CSSProperties}
                  >
                    <span className="sw" aria-hidden="true" />
                    {PLATES[k].label}
                  </button>
                ))}
              </div>
            </div>
            <div className="ad-pair">
              <Field id="ad-deal" label="Deal sticker (optional)" error={errors.dealLabel} help={`Up to ${MAX_DEAL} characters, like “-30%”.`}>
                <input
                  id="ad-deal"
                  className="input"
                  autoComplete="off"
                  maxLength={MAX_DEAL}
                  value={d.dealLabel}
                  aria-invalid={invalid('dealLabel')}
                  aria-describedby={described('ad-deal', 'dealLabel', true)}
                  onChange={input('dealLabel')}
                />
              </Field>
              <Field
                id="ad-permit"
                label={d.dealLabel.trim() || d.category === 'supplement' ? 'Permit line' : 'Permit line (optional)'}
                error={errors.permitText}
                help={
                  d.category === 'supplement'
                    ? 'Required: the FDA “MAHALAGANG PAALALA” warning or the FDA registration.'
                    : d.dealLabel.trim()
                      ? 'Required with a deal: the DTI promo permit, like “DTI FTEB Permit No. 0000 s.2026”.'
                      : 'Shown in small mono under the headline.'
                }
              >
                <input
                  id="ad-permit"
                  className="input t-mono"
                  autoComplete="off"
                  maxLength={MAX_PERMIT}
                  value={d.permitText}
                  aria-invalid={invalid('permitText')}
                  aria-describedby={described('ad-permit', 'permitText', true)}
                  onChange={input('permitText')}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Media</legend>
            <div className="ad-file">
              <label htmlFor="ad-image" className="t-label">
                {d.kind === 'video' ? 'Poster frame (photo)' : 'Photo'}
              </label>
              <span className="help t-caption muted">JPG, PNG or WebP, 300 KB at most. Best: 1200 × 628, WebP, 150 KB or less. No logo or text baked in.</span>
              <input
                ref={imageInput}
                id="ad-image"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                aria-invalid={invalid('image')}
                aria-describedby={errors.image ? 'ad-image-err' : undefined}
                onChange={(e) => void pickImage(e)}
              />
              {image ? (
                <span className="t-caption">
                  {image.file.name} · uploads when you save.{image.advice.length ? ` ${image.advice.join(' ')}` : ''}
                </span>
              ) : ad?.imageUrl ? (
                <span className="t-caption muted">Uploaded. Pick another file to replace it.</span>
              ) : null}
              <Err id="ad-image-err">{errors.image}</Err>
            </div>
            {d.kind === 'video' ? (
              <div className="ad-file">
                <label htmlFor="ad-video" className="t-label">
                  Video
                </label>
                <span className="help t-caption muted">MP4 only, 1.1 MB at most, 6–15 s (6–10 s is best). No sound needed: it always plays muted, so burn the captions in. Brand and offer in the first 3 s.</span>
                <input
                  ref={videoInput}
                  id="ad-video"
                  type="file"
                  accept="video/mp4"
                  aria-invalid={invalid('video')}
                  aria-describedby={errors.video ? 'ad-video-err' : undefined}
                  onChange={(e) => void pickVideo(e)}
                />
                {video ? (
                  <span className="t-caption">
                    {video.file.name} · uploads when you save. {video.advice.join(' ')}
                  </span>
                ) : ad?.videoUrl ? (
                  <span className="t-caption muted">Uploaded. Pick another file to replace it.</span>
                ) : null}
                <Err id="ad-video-err">{errors.video}</Err>
              </div>
            ) : null}
          </fieldset>

          <fieldset>
            <legend>Where a tap goes</legend>
            <Field id="ad-link" label="Opens">
              <Select id="ad-link" value={d.linkKind} options={LINK_OPTIONS} onChange={(e) => set('linkKind', e.target.value as AdLinkKind)} />
            </Field>
            {d.linkKind === 'shop' ? (
              <Field id="ad-target" label="Shop" error={errors.linkTarget}>
                <Select id="ad-target" value={d.linkTarget} options={shopOptions} placeholder="Choose a shop" onChange={input('linkTarget')} aria-invalid={invalid('linkTarget')} aria-describedby={described('ad-target', 'linkTarget')} />
              </Field>
            ) : d.linkKind === 'line' ? (
              <Field id="ad-target" label="Line" error={errors.linkTarget}>
                <Select id="ad-target" value={d.linkTarget} options={LINE_TARGETS} placeholder="Choose a line" onChange={input('linkTarget')} aria-invalid={invalid('linkTarget')} aria-describedby={described('ad-target', 'linkTarget')} />
              </Field>
            ) : d.linkKind === 'product' ? (
              <Field id="ad-target" label="Product ID" error={errors.linkTarget} help="The product's ID, from Ops → Products.">
                <input
                  id="ad-target"
                  className="input t-mono"
                  autoComplete="off"
                  value={d.linkTarget}
                  aria-invalid={invalid('linkTarget')}
                  aria-describedby={described('ad-target', 'linkTarget', true)}
                  onChange={input('linkTarget')}
                />
              </Field>
            ) : null}
          </fieldset>

          <fieldset>
            <legend>Schedule and slot</legend>
            <span className="help t-caption muted">Manila time. The ad shows only while it's live and inside these dates.</span>
            <div className="ad-pair">
              <Field id="ad-start-day" label="Starts">
                <DateField id="ad-start-day" value={d.startDay} onChange={input('startDay')} aria-invalid={invalid('schedule')} />
              </Field>
              <Field id="ad-start-time" label="Start time">
                <TimeField id="ad-start-time" value={d.startTime} onChange={input('startTime')} aria-invalid={invalid('schedule')} />
              </Field>
              <Field id="ad-end-day" label="Ends">
                <DateField id="ad-end-day" value={d.endDay} min={d.startDay || undefined} onChange={input('endDay')} aria-invalid={invalid('schedule')} />
              </Field>
              <Field id="ad-end-time" label="End time">
                <TimeField id="ad-end-time" value={d.endTime} onChange={input('endTime')} aria-invalid={invalid('schedule')} />
              </Field>
            </div>
            <Err id="ad-schedule-err">{errors.schedule}</Err>
            <Field
              id="ad-priority"
              label="Priority"
              error={errors.priority}
              help="Higher shows first. Up to 3 ads rotate on Home; the highest takes slot 1, the premium slot."
            >
              <input
                id="ad-priority"
                className="input"
                inputMode="numeric"
                autoComplete="off"
                value={d.priority}
                aria-invalid={invalid('priority')}
                aria-describedby={described('ad-priority', 'priority', true)}
                onChange={input('priority')}
              />
            </Field>
          </fieldset>

          {errors.form ? (
            <p className="pill st-error err-pill" role="alert">
              {errors.form}
            </p>
          ) : null}
          <div className="row wrap">
            <button type="submit" className="btn btn-primary" disabled={saving} aria-busy={saving || undefined}>
              <Icon name="check" />
              {saving ? 'Saving…' : ad ? 'Save changes' : 'Save draft'}
            </button>
            <button type="button" className="btn btn-quiet" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>

        <aside className="ad-side" aria-label="Preview and go live">
          <section className="card panel" aria-labelledby="ad-prev-h">
            <h2 id="ad-prev-h">Preview</h2>
            <AdPreview
              kind={d.kind}
              advertiser={d.advertiserName}
              headline={d.headline}
              plate={d.plate}
              deal={d.dealLabel.trim() || null}
              permitText={d.permitText.trim() || null}
              clearanceCode={d.clearanceCode.trim() || null}
              imageSrc={imageSrc}
              videoSrc={d.kind === 'video' ? videoSrc : null}
            />
            <span className="t-caption muted">As it shows at the top of Home, on a 375-wide phone.</span>
          </section>

          <section className="card panel" aria-labelledby="ad-ready-h">
            <h2 id="ad-ready-h">Ready to go live?</h2>
            <Checklist items={checklist} label="Go-live checklist" />
            {status.notReady && ad && status.notReady.ad.id === ad.id ? (
              <NotReadyPanel headline={ad.headline} missing={status.notReady.missing} onClose={status.clearNotReady} />
            ) : null}
            {ad ? (
              <>
                <div className="row wrap">
                  {statusActions(ad.status).map((a) => (
                    <button
                      key={a.to}
                      type="button"
                      className={a.to === 'live' ? 'btn btn-primary' : a.to === 'archived' ? 'btn btn-danger' : 'btn'}
                      disabled={dirty || status.isPending}
                      onClick={() => status.mutate({ ad, to: a.to })}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                {dirty ? <span className="t-caption muted">Save your changes first.</span> : null}
              </>
            ) : (
              <span className="t-caption muted">Save the draft first, then put it live from here.</span>
            )}
          </section>

          <SpecSheet />
        </aside>
      </div>
    </>
  );
}

/** The advertiser spec sheet (preview.html "Advertiser spec sheet"). */
export function SpecSheet() {
  return (
    <details className="card panel ad-spec">
      <summary>Advertiser spec sheet</summary>
      <p className="t-caption muted">What a shop or brand uploads. PopCart converts it and enforces the limits.</p>
      <dl>
        <dt>Photo</dt>
        <dd>
          <em>1200 × 628</em> (1.91:1), or 1280 × 720 cropped to the centre. Delivered as WebP <em>≤ 150 KB</em>. Product inside the safe zone. No logo or text baked into the photo.
        </dd>
        <dt>Video</dt>
        <dd>
          <em>6–15 s</em> (6–10 s best), 1.91:1 or 16:9, at least 1280 px wide. Brand and offer in the <em>first 3 s</em>, captions burned in, plus a designed poster frame. We deliver H.264 MP4, 960 × 504, no sound, <em>≤ 1.1 MB</em>.
        </dd>
        <dt>Words</dt>
        <dd>
          Headline <em>≤ 25 characters</em>, set by us in Archivo. Plate colour picked from the brand palette. Logo: separate 1:1, <em>≥ 120 px</em>, ≤ 50 KB.
        </dd>
        <dt>Never</dt>
        <dd>Flashing (more than 3 per second), fake buttons or notifications, countdowns, sound without a tap, loops past 15 s.</dd>
        <dt>The law</dt>
        <dd>
          We collect the advertiser's DTI/SEC registration, plus permits for regulated goods (RA 11967 §22b). ASC clearance codes are required for alcohol, supplements and OTC medicines, and supplement ads must carry the FDA "MAHALAGANG PAALALA" warning. We
          don't accept vapes and tobacco, gambling and e-sabong, political ads, prescription drugs, breast-milk substitutes or adult content. Takedowns happen within 24 h.
        </dd>
      </dl>
    </details>
  );
}
